/**
 * api/client.ts — Typed Hono RPC client (lightweight wrapper).
 *
 * Phase A imported `hc<AppType>` directly but `typeof apiRouter` (after chained
 * `.use(...)` + `.route(...)` calls) confuses the `hc<T>` constraint solver
 * and collapses `client` to `unknown` in TypeScript. Plan §10's recommendation
 * ("use `hc<typeof apiRouter>` directly") is sound at runtime but produces an
 * unsatisfiable type in the editor.
 *
 * Phase B sidesteps the typing squeeze by:
 *
 *   1. Re-exporting the `apiRouter` `AppType` for any consumer that wants it.
 *   2. Exposing `fetchApi(path, init)` as a small `fetch` wrapper that the
 *      TanStack Query factories (and the rest of the app) can call. That keeps
 *      the response shape `{ ok, data | error }` consistent with the Hono
 *      contract while sidestepping `hc`'s generic constraints.
 *   3. Keeping the `hc` client as a passthrough for code paths that prefer the
 *      RPC style; it's typed as `ReturnType<typeof hc<AppType>>` so it
 *      follows whatever Hono decides on (currently `unknown`, but the
 *      consumer-side usage is enough to typecheck downstream with the help of
 *      `InferResponseType` when desired).
 */

import { hc } from 'hono/client';
import type { apiRouter } from '../../api/router.js';

export type AppType = typeof apiRouter;
export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = { ok: false; error: { code: string; message: string } };
export type ApiResponse<T> = ApiOk<T> | ApiErr;

const API_BASE = (import.meta.env?.VITE_SITE_URL as string | undefined) ?? '';

/**
 * Thin typed fetch wrapper. Always parses the body as JSON and asserts the
 * `{ok, data|error}` contract. Use this for TanStack Query factories; the
 * raw `client` (Hono RPC) is also exported for code paths that prefer the
 * typed chain.
 */
export async function fetchApi<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<ApiResponse<T>> {
  const { json, headers, ...rest } = init;
  const finalInit: RequestInit = {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(headers as Record<string, string> | undefined),
    },
    credentials: 'include',
  };

  if (json !== undefined) {
    finalInit.body = JSON.stringify(json);
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, finalInit);

  try {
    return (await res.json()) as ApiResponse<T>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'parse_error',
        message: 'Respons server tidak dapat dibaca.',
      },
    };
  }
}

/**
 * Hono RPC passthrough. Use for the rare case where you need the chain
 * syntax (`client.games.$get()`). Phase B does not lean on this; queries
 * use `fetchApi` for clarity.
 */
export const client = hc<AppType>(`${API_BASE}/api`);
